import { useEffect, useRef, useState } from "react";
import { useCoverPin } from "@/hooks/use-cover-pin";
import { trackCta } from "@/lib/analytics/cta";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import ScheduleAnimation from "./howItWorks/ScheduleAnimation";
import MatchAnimation from "./howItWorks/MatchAnimation";
import ContentAnimation from "./howItWorks/ContentAnimation";

/**
 * Dark sticky section. Centered heading on top, three step cards below.
 * Each card carries an ambient UI mockup animation that cycles through an
 * orchestrated sequence (Animation 1 → 2 → 3 → loop) with brief overlap
 * between consecutive animations. When a card isn't actively playing, it
 * shows a populated "resting" state with subtle ambient micro-motion.
 */
const STEPS = [
  {
    tag: "Book It",
    title: "Share your schedule",
    body: "Create a reservation in your member portal, sharing details about the event you want covered and how you want it captured.",
    Animation: ScheduleAnimation,
  },
  {
    tag: "Get Covered",
    title: "Your creator shows up",
    body: "We match you to a local, background-checked creator who arrives ready to hit the ground running. The right human, not the closest camera.",
    Animation: MatchAnimation,
  },
  {
    tag: "Earn Yours",
    title: "Share ready-to-post content",
    body: "Edited photos and reels are delivered in your portal within 48 hours, one-click share to Instagram, TikTok, YouTube or anywhere your audience lives.",
    Animation: ContentAnimation,
  },
];

// Orchestrated cycle (total ~31s). Cards 1 & 2 now run build-up + send-off
// (~12s each); Card 3 is build-up only (~8s). Staggered A1→A2→A3 with a ~1s
// overlap as each hands off to the next.
//   t=0      → A1 active (populate → particle dissolve)
//   t=11000  → A2 active (A1 still finishing its send-off)
//   t=12000  → A1 → resting (created state)
//   t=22000  → A3 active (A2 still finishing its send-off)
//   t=23000  → A2 → resting (matched state)
//   t=30000  → A3 → resting (followed by ~1s pause)
//   t=31000  → restart
const CYCLE_MS = 31000;
const SCHEDULE: { at: number; active: [boolean, boolean, boolean] }[] = [
  { at: 0, active: [true, false, false] },
  { at: 11000, active: [true, true, false] },
  { at: 12000, active: [false, true, false] },
  { at: 22000, active: [false, true, true] },
  { at: 23000, active: [false, false, true] },
  { at: 30000, active: [false, false, false] },
];

const HowItWorksDark = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const coverTop = useCoverPin(sectionRef);
  const [revealed, setRevealed] = useState(false);
  const [inView, setInView] = useState(true);
  const [activeStates, setActiveStates] = useState<[boolean, boolean, boolean]>([
    true,
    false,
    false,
  ]);

  // One-shot reveal cascade once the section scrolls into view. Triggered via a
  // bottom rootMargin rather than a height fraction, so it fires reliably whether
  // the section is exactly one viewport tall (desktop) or taller than the viewport
  // (mobile, where the three cards stack vertically).
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: "0px 0px -25% 0px" },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // Pause inner animations whenever the section is fully offscreen.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // Orchestrate the active/resting cycle once the section is revealed and in view.
  useEffect(() => {
    if (!revealed || !inView) return;

    const timeouts: number[] = [];

    const runCycle = () => {
      timeouts.forEach((id) => window.clearTimeout(id));
      timeouts.length = 0;

      SCHEDULE.forEach(({ at, active }) => {
        timeouts.push(window.setTimeout(() => setActiveStates(active), at));
      });
      timeouts.push(window.setTimeout(runCycle, CYCLE_MS));
    };

    runCycle();
    return () => timeouts.forEach((id) => window.clearTimeout(id));
  }, [revealed, inView]);

  const scrollToLeadForm = () => {
    trackCta({ cta_id: "get_game_plan", location: "how_it_works", destination: "#lead-form" });
    const target = document.getElementById("lead-form");
    if (!target) return;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      style={{ top: coverTop }}
      className={`relative md:sticky bg-black text-white py-14 sm:py-20 md:py-36 ${
        inView ? "" : "[&_.hiw-anim]:[animation-play-state:paused]"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <h2 className="text-center text-3xl md:text-5xl font-black italic uppercase leading-[1.05] text-white">
          How a Fivestar Membership Works
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((step, i) => (
            <div
              key={i}
              style={{ transitionDelay: revealed ? `${i * 150}ms` : "0ms" }}
              className={`relative rounded-[32px] bg-[rgb(14,14,14)] overflow-hidden flex flex-col transition-all duration-700 ease-out ${
                revealed
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
            >
              <step.Animation isActive={activeStates[i]} />
              {/* Large faint outline numeral — anchored to the CARD's
                  bottom-left corner, bleeding off the left + bottom edges
                  (clipped by the card's overflow-hidden). */}
              <span
                aria-hidden
                className="pointer-events-none select-none absolute bottom-0 left-0 z-0 -translate-x-[6%] translate-y-1/4 text-[13rem] leading-none font-extrabold not-italic text-transparent"
                style={{ WebkitTextStroke: "1px rgba(255,255,255,0.2)" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="relative z-10 p-6 md:p-8 flex flex-col gap-4">
                <span className="self-start inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-300">
                  {step.tag}
                </span>
                <h3 className="text-xl md:text-2xl font-semibold text-white">
                  {step.title}
                </h3>
                <p className="text-base text-zinc-400 leading-relaxed">
                  {step.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{ transitionDelay: revealed ? "450ms" : "0ms" }}
          className={`mt-12 md:mt-16 flex justify-center transition-all duration-700 ease-out ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <button
            type="button"
            onClick={scrollToLeadForm}
            className="inline-flex items-center gap-2.5 bg-cyan hover:bg-cyan/90 text-black font-black italic uppercase tracking-wide whitespace-nowrap pl-8 pr-9 py-4 text-base md:text-lg rounded-full shadow-[0_4px_14px_rgba(68,194,227,0.25)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-black"
          >
            Get Your Game Plan
            <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksDark;
